import {
  object,
  string,
  SchemaOf,
  number,
  array,
  mixed,
  lazy,
  AnySchema,
} from 'yup'
import {
  DeviceType,
  StudentUpdateFormValues,
  TeacherContact,
  TeacherUpdateFormValues,
} from 'types'
import { CreateLocationProps, LocationType } from 'types/location'
import { msFromTimeString } from 'utils/helpers'
import { ANDROID, GENDER_OPTIONS } from '../../constants'

type UserFormProps = Pick<
  StudentUpdateFormValues,
  | 'forename'
  | 'surname'
  | 'email'
  | 'gender'
  | 'countryCode'
  | 'phone'
  | 'source'
  | 'campaignName'
  | 'orgName'
>

type TeacherFormProps = Pick<
  TeacherUpdateFormValues,
  | 'forename'
  | 'surname'
  | 'email'
  | 'countryCode'
  | 'defaultTeachingOrg'
  | 'contact'
  | 'avgInstructionLength'
  | 'teachingLocations'
> & { contact: TeacherContact }

type LoginFormProps = {
  email: string
}

type VerificationCodeFormProps = {
  code: string
}

export const StudentFormSchema: SchemaOf<UserFormProps> = object({
  id: string(),
  forename: string()
    .min(2, 'First name is too short.')
    .required('First name is required.'),
  surname: string()
    .min(2, 'Last name is too short.')
    .required('Last name is required.'),
  email: string().email('The email is invalid.').required('Email is required.'),
  instructionDate: string().required('Instruction date is required.'),
  countryCode: string().required('Select a country.'),
  phone: string()
    .min(9, 'Phone number is too short.')
    .max(18, 'Phone number is too long.'),
  gender: mixed().oneOf(GENDER_OPTIONS.map(({ value }) => value)),
  netsuiteId: string(),
  isTester: string(),
  dateOfBirth: string(),
  __day: string(),
  __month: string(),
  __year: string(),
  source: string(),
  campaignName: string(),
  orgName: string(),
})
export const TeacherFormSchema: SchemaOf<UserFormProps> = object({
  id: string(),
  forename: string()
    .min(2, 'First name is too short.')
    .required('First name is required.'),
  surname: string()
    .min(2, 'Last name is too short.')
    .required('Last name is required.'),
  countryCode: string().required('Select a country.'),
  googlePlayEmail: string().when(
    ['device'],
    (device: DeviceType, schema: any) => {
      return device === ANDROID
        ? schema
            .email('The email is invalid.')
            .required('Google play email is required.')
        : schema
    }
  ),
  email: string().email('The email is invalid.').required('Email is required.'),
  instructionDate: string().required('Instruction date is required.'),
  phone: string()
    .min(9, 'Phone number is too short.')
    .max(18, 'Phone number is too long.'),
  gender: mixed().oneOf(GENDER_OPTIONS.map(({ value }) => value)),
  netsuiteId: string(),
  isTester: string(),
  dateOfBirth: string(),
  __day: string(),
  __month: string(),
  __year: string(),
  source: string(),
  campaignName: string(),
  orgName: string(),
})
export const NonPublicCountrySchema = object({
  device: string().required('Phone type is Required.'),
  googlePlayEmail: string().when(
    ['device'],
    (device: DeviceType, schema: any) => {
      return device === ANDROID
        ? schema
            .email('The email is invalid.')
            .required('Google play email is required.')
        : schema
    }
  ),
})

export const UserTypeValidation = object({
  userType: string().required('Student type is required.'),
})

export const LocationValidation = object({
  locationId: string()
    .nullable()
    .test(
      'locationIsNotNull',
      'Choose a location or select "Not confirmed".',
      (val: any) => {
        return val !== null
      }
    ),
})

export const TimeValidation = object({
  startTime: string()
    .nullable()
    .test(
      'startIsNotNull',
      'Enter a time or select "Not confirmed".',
      (val: any) => {
        return val !== null
      }
    ),
  endTime: string()
    .nullable()
    .test(
      'endIsNotNull',
      'Enter a time or select "Not confirmed".',
      (val: any) => {
        return val !== null
      }
    )
    .when(['startTime'], (startTime: string, schema: any) => {
      return startTime
        ? schema.test(
            'endAfterStart',
            'End time must be after start.',
            (endTime: any) => {
              const startMs = msFromTimeString(startTime)
              const endMs = msFromTimeString(endTime)
              return (
                startMs === undefined ||
                endMs === undefined ||
                endTime >= startTime
              )
            }
          )
        : schema
    }),
})

export const ContactOptionValidation = object({
  contactOption: string()
    .nullable()
    .test(
      'contactOptionIsNotNull',
      'Please select a contact option',
      (val: any) => {
        return val !== null
      }
    ),
})

export const NetsuiteValidation = object({
  netsuiteId: string().required('Netsuite record is required.'),
})

//3730 clean up
export const TeachingOrganizationValidation = object({
  teachingOrg: string().required('Teaching organization is required.'),
})

export const TeachingOrganizationIdValidation = object({
  teachingOrgId: string().required('Teaching organization is required.'),
})

export const LoginFormSchema: SchemaOf<LoginFormProps> = object({
  email: string()
    .email('Please enter a valid email address.')
    .required('The email field is required.'),
})

export const VerificationCodeFormSchema: SchemaOf<VerificationCodeFormProps> =
  object({
    code: string()
      .min(3, 'Verification code is too short.')
      .max(4, 'Invalid verification code.')
      .required(
        'No verification code was entered. Enter the code you received by email.'
      ),
  })

export const BasicTeacherSchema: SchemaOf<TeacherFormProps> = object({
  id: string().required(),
  forename: string()
    .min(2, 'First name is too short.')
    .required('First name is required.'),
  surname: string()
    .min(2, 'Last name is too short.')
    .required('Last name is required.'),
  email: string().email('The email is invalid.').required('Email is required.'),
  countryCode: string().required('Select a country.'),
  defaultTeachingOrg: string(),
  contact: lazy((contact: TeacherContact) => {
    const defaultValidation = object()
      .required()
      .shape({
        phone: string()
          .min(9, 'Phone number is too short.')
          .max(18, 'Phone number is too long.'),
        email: string()
          .email('The email is invalid.')
          .required('Email is required for each contact method.'),
      })
    const contactIds = Object.keys(contact)
    const obj = {
      default: defaultValidation,
    } as {
      [key: string]: AnySchema
    }
    contactIds.forEach((id: string) => {
      obj[id] = obj['default']
    })
    return object().required().shape(obj)
  }) as unknown as SchemaOf<TeacherContact>,
  avgInstructionLength: number(),
  teachingLocations: array(),
})

export const LocationSchema: SchemaOf<CreateLocationProps> = object({
  name: string().required('A name is required.'),
  addressLine: string().required('Address line is required.'),
  countryCode: string().required('Select a country.'),
  city: string().required('Enter a city.'),
  zip: string(),
  state: string(),
  specialInstructions: string(),
  coordinates: object({
    long: string(),
    lat: string(),
  }),
  type: mixed<LocationType>().oneOf(['TMCenter']).required('TYPE REQUIRED'),
})
